import { calculatePackagePrice } from '../Package/PackageUtils';
import { calculateExtensionTotal, determineExpirationDate } from '../Order/OrderUtils';

export const CART_STORAGE_VERSION = 2;
export const PACKAGE_ADDED_VIA = 'predefined_package';
export const CUSTOM_ADDED_VIA = 'build_your_own';

const toPositiveInt = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const toNullableString = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

const isValidIsoDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const toIsoDateString = (value) => {
  if (!isValidIsoDate(value)) return null;
  return value;
};

const toRoundedNumber = (value) => Number(Number(value || 0).toFixed(2));

const getPackageDetails = (cartData, packageId) => {
  const normalizedPackageId = toPositiveInt(packageId);
  if (!normalizedPackageId) return null;

  const packageDetails = cartData && cartData.package_details ? cartData.package_details : {};
  const packageKey = String(normalizedPackageId);
  const details = packageDetails[packageKey] || packageDetails[normalizedPackageId];
  if (!details) return null;

  const numKeys = toPositiveInt(details.numKeys);
  const numMonths = toPositiveInt(details.numMonths);
  if (!numKeys || !numMonths) return null;

  return {
    numKeys,
    numMonths,
  };
};

const getExtraOptionMonths = (cartData, extraOptionId) => {
  const normalizedExtraOptionId = toNullableString(extraOptionId);
  if (!normalizedExtraOptionId) return 0;

  const extraOptionsMonths = cartData && cartData.extra_options_months ? cartData.extra_options_months : {};
  const months = toPositiveInt(extraOptionsMonths[normalizedExtraOptionId]);
  return months || 0;
};

export const isPackageLine = (line) => {
  if (!line || typeof line !== 'object') return false;
  if (line.addedVia === CUSTOM_ADDED_VIA) return false;
  return line.addedVia === PACKAGE_ADDED_VIA || toPositiveInt(line.packageId) !== null;
};

export const derivePackageMonths = ({ packageId, extraOptionId, cartData }) => {
  const packageDetails = getPackageDetails(cartData, packageId);
  if (!packageDetails) return null;
  return packageDetails.numMonths + getExtraOptionMonths(cartData, extraOptionId);
};

export const derivePackageKeys = ({ packageId, cartData }) => {
  const packageDetails = getPackageDetails(cartData, packageId);
  return packageDetails ? packageDetails.numKeys : null;
};

export const buildPackageCartLine = ({ line = {}, packageId, extraOptionId, startDate, cartData, vatRate = 25 }) => {
  const normalizedPackageId = toPositiveInt(packageId);
  const normalizedExtraOptionId = toNullableString(extraOptionId);
  const normalizedStartDate = toIsoDateString(startDate || line.startDate);
  const packageDetails = getPackageDetails(cartData, normalizedPackageId);

  if (!normalizedPackageId || !normalizedStartDate || !packageDetails) return null;

  const months = packageDetails.numMonths + getExtraOptionMonths(cartData, normalizedExtraOptionId);
  const keys = packageDetails.numKeys;
  const { packageTotal, packageTotalNoVat } = calculatePackagePrice({
    selectedPackage: normalizedPackageId,
    packages: (cartData && cartData.packages) || {},
    vatRate,
  });
  const extensionTotal = calculateExtensionTotal(getExtraOptionMonths(cartData, normalizedExtraOptionId), {
    selectedPackage: normalizedPackageId,
    packageDetails: (cartData && cartData.package_details) || {},
    numberOfKeys: keys,
    perKeyPrice: (cartData && cartData.per_key_price) || 0,
    perKeyPriceNoDiscount: (cartData && cartData.per_key_price_no_discount) || 0,
    useDiscount: true,
  });
  const vatFactor = 1 + (vatRate || 0) / 100;
  const discountedTotal = packageTotal + extensionTotal;
  const discountedTotalNoVat = packageTotalNoVat + extensionTotal / vatFactor;
  const expirationDate = determineExpirationDate(normalizedStartDate, months);

  return {
    ...line,
    addedVia: PACKAGE_ADDED_VIA,
    packageId: normalizedPackageId,
    extraOptionId: normalizedExtraOptionId,
    keys,
    months,
    startDate: normalizedStartDate,
    expirationDate: new Date(expirationDate).toISOString().slice(0, 10),
    discountedTotal: toRoundedNumber(discountedTotal),
    discountedTotalNoVat: toRoundedNumber(discountedTotalNoVat),
    game: null,
  };
};

const normalizeCustomCartLine = (line) => {
  const keys = toPositiveInt(line.keys);
  const months = toPositiveInt(line.months);
  const startDate = toIsoDateString(line.startDate);

  if (!keys || !months || !startDate) return null;

  const expirationDate = determineExpirationDate(startDate, months);

  return {
    ...line,
    addedVia: CUSTOM_ADDED_VIA,
    packageId: null,
    extraOptionId: null,
    keys,
    months,
    startDate,
    expirationDate: new Date(expirationDate).toISOString().slice(0, 10),
  };
};

export const normalizeCartLine = ({ line, cartData, vatRate = 25 }) => {
  if (!line || typeof line !== 'object') return null;
  if (isPackageLine(line)) {
    return buildPackageCartLine({
      line,
      packageId: line.packageId,
      extraOptionId: line.extraOptionId,
      startDate: line.startDate,
      cartData,
      vatRate,
    });
  }

  return normalizeCustomCartLine(line);
};

export const normalizeCartState = (cart, vatRate = 25) => {
  const cartData = cart && typeof cart === 'object' ? cart : {};
  const lines = Array.isArray(cartData.lines) ? cartData.lines : [];
  const normalizedLines = lines
    .map((line) => normalizeCartLine({ line, cartData, vatRate }))
    .filter(Boolean);

  return {
    ...cartData,
    storageVersion: CART_STORAGE_VERSION,
    lines: normalizedLines,
  };
};

export const getCheckoutLine = (cart, vatRate = 25) => {
  const normalizedCart = normalizeCartState(cart, vatRate);
  return normalizedCart.lines[0] || null;
};



// WEBPACK FOOTER //
// ./src/c/PremiumKeys/Cart/CartState.js